import type {
  StageUpdateInDto,
  WorkflowStageOutDto,
} from 'shared/domain/workflow/types';
import {
  ApprovalType,
  getDefaultAutoApproveForApprovalType,
} from 'shared/domain/workflow/types';
import {
  areWorkflowApproversEqualByOrder,
  normalizeWorkflowApprovers,
} from 'shared/domain/workflow/stageApprovers';
import { areArraysShallowEqual, isNonEmptyArray } from 'shared/utils/array';
import type {
  WorkflowStageRow,
  WorkflowOriginalSnapshot,
  WorkflowSaveDiff,
} from '../types';
import { buildStageCreatePayload } from './rows';

export function computeStagePatch(
  serverStage: WorkflowStageOutDto,
  row: WorkflowStageRow
): StageUpdateInDto | null {
  const patch: StageUpdateInDto = {};
  let changed = false;

  const nextName = row.name.trim();
  if (nextName !== (serverStage.name ?? '').trim()) {
    patch.name = nextName;
    changed = true;
  }

  const nextDescription = row.description ?? '';
  const prevDescription = serverStage.description ?? '';
  if (nextDescription !== prevDescription) {
    patch.description = row.description;
    changed = true;
  }

  const nextIcon = row.icon ?? null;
  const prevIcon = serverStage.icon ?? null;
  if (nextIcon !== prevIcon) {
    patch.icon = nextIcon;
    changed = true;
  }

  const prevApprovalType = serverStage.approvalType ?? ApprovalType.NONE;
  if (row.approvalType !== prevApprovalType) {
    patch.approvalType = row.approvalType;
    patch.autoApprove = getDefaultAutoApproveForApprovalType(row.approvalType);
    changed = true;
  } else {
    const prevAutoApprove =
      typeof serverStage.autoApprove === 'boolean'
        ? serverStage.autoApprove
        : getDefaultAutoApproveForApprovalType(prevApprovalType);
    if (row.autoApprove !== prevAutoApprove) {
      patch.autoApprove = row.autoApprove;
      changed = true;
    }
  }

  const prevApprovers = normalizeWorkflowApprovers(serverStage.approvers);
  const nextApprovers = normalizeWorkflowApprovers(row.approvers);
  if (!areWorkflowApproversEqualByOrder(prevApprovers, nextApprovers)) {
    patch.approvers = nextApprovers;
    changed = true;
  }

  const prevRequiredApprovals = serverStage.requiredApprovals ?? null;
  const nextRequiredApprovals = row.requiredApprovals;
  if (nextRequiredApprovals !== prevRequiredApprovals) {
    patch.requiredApprovals = nextRequiredApprovals;
    changed = true;
  }

  const nextDeadline = row.deadlineDays;
  const prevDeadline = serverStage.deadlineDays;
  if (nextDeadline !== prevDeadline) {
    patch.deadlineDays = nextDeadline;
    changed = true;
  }

  return changed ? patch : null;
}

export function computeSaveDiff(
  original: WorkflowOriginalSnapshot,
  workflowName: string,
  localRows: readonly WorkflowStageRow[]
): WorkflowSaveDiff {
  const trimmedName = workflowName.trim();
  const workflowNameUpdate =
    trimmedName.length > 0 && trimmedName !== original.name.trim()
      ? { name: trimmedName }
      : undefined;

  const localServerIds = new Set<string>();
  const toUpdate: WorkflowSaveDiff['toUpdate'] = [];
  const toCreate: WorkflowSaveDiff['toCreate'] = [];
  const persistedServerIdByLocalId = new Map<string, string>();

  localRows.forEach((row, rowIndex) => {
    if (row._serverId === undefined) {
      toCreate.push({
        localId: row._localId,
        rowIndex,
        createBody: buildStageCreatePayload(row),
        deadlineDays: row.deadlineDays,
        description: row.description,
      });
      return;
    }
    persistedServerIdByLocalId.set(row._localId, row._serverId);
    localServerIds.add(row._serverId);
    const serverStage = original.stagesByServerId.get(row._serverId);
    if (!serverStage) {
      return;
    }
    const patch = computeStagePatch(serverStage, row);
    if (patch) {
      toUpdate.push({ serverId: row._serverId, dto: patch });
    }
  });

  const toDelete: string[] = [];
  for (const id of original.stagesByServerId.keys()) {
    if (!localServerIds.has(id)) {
      toDelete.push(id);
    }
  }

  const desiredOrderLocalIds = localRows.map((r) => r._localId);

  const currentOrderServerIds = localRows
    .map((r) => r._serverId)
    .filter((id): id is string => id !== undefined);
  const orderChanged = !areArraysShallowEqual(
    currentOrderServerIds,
    original.originalOrderServerIds.filter((id) => !toDelete.includes(id))
  );

  return {
    workflowNameUpdate,
    toDelete,
    toUpdate,
    toCreate,
    desiredOrderLocalIds,
    persistedServerIdByLocalId,
    baselineServerStageIds: new Set(original.stagesByServerId.keys()),
    orderChanged,
  };
}

/** Whether a draft differs from the snapshot it started from: what a save would change. */
export function hasWorkflowChanges({ workflowNameUpdate, toCreate, toUpdate, toDelete, orderChanged }: WorkflowSaveDiff): boolean {
  return (
    Boolean(workflowNameUpdate) ||
    isNonEmptyArray(toCreate) ||
    isNonEmptyArray(toUpdate) ||
    isNonEmptyArray(toDelete) ||
    orderChanged
  );
}
